import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Button, Card, Icon, I, MOBILE_SINGLE_COLUMN, Pill, TEXT_WRAP_STYLE, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { getInventoryProductPullRunIssues } from '../inventory/inventoryApi'
import type {
  InventoryProductPullRun,
  InventoryProductPullRunIssue,
  InventoryProductPullRunIssueGroup,
  InventoryProductPullRunIssuesPage,
  InventoryProductPullRunStatus,
} from '../inventory/types'

interface InventoryZohoPullRunDetailPageProps {
  accessToken: string
  inventoryConnectionId: number | null
  runId: number | null
  onBack: () => void
}

const PAGE_SIZE = 50

function runTone(status: InventoryProductPullRunStatus): 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral' {
  if (status === 'SUCCEEDED') return 'success'
  if (status === 'SUCCEEDED_WITH_ISSUES') return 'warning'
  if (status === 'FAILED') return 'danger'
  if (status === 'RATE_LIMITED') return 'warning'
  return 'info'
}

function errorKey(error: unknown): string {
  if (error instanceof ApiClientError) return `pages.inventoryZohoPullRunDetail.errors.${error.code}`
  return 'pages.inventoryZohoPullRunDetail.errors.REQUEST_FAILED'
}

function formatDateTime(value: string | null | undefined, locale: string, fallback: string) {
  if (!value) return fallback
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function Stat({ label, value, body }: { label: string; value: string; body?: string }) {
  return (
    <Card padding={16} style={{ background: 'var(--ink-50)' }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{label}</div>
      <div style={{ marginTop: 6, fontSize: 22, fontWeight: 900, color: 'var(--brand-900)' }}>{value}</div>
      {body && <div style={{ marginTop: 4, color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.45 }}>{body}</div>}
    </Card>
  )
}

function issueTitle(t: (key: string, options?: Record<string, unknown>) => unknown, code: string) {
  return String(t(`pages.inventoryZohoPullRunDetail.issueCodes.${code}.title`, {
    defaultValue: String(t('pages.inventoryZohoPullRunDetail.issueCodes.UNKNOWN.title')),
  }))
}

function issueAction(t: (key: string, options?: Record<string, unknown>) => unknown, code: string) {
  return String(t(`pages.inventoryZohoPullRunDetail.issueCodes.${code}.action`, {
    defaultValue: String(t('pages.inventoryZohoPullRunDetail.issueCodes.UNKNOWN.action')),
  }))
}

function failureTitle(t: (key: string, options?: Record<string, unknown>) => unknown, code: string) {
  return String(t(`pages.inventoryZohoPullRunDetail.failureCodes.${code}.title`, {
    defaultValue: String(t('pages.inventoryZohoPullRunDetail.failureCodes.UNKNOWN.title')),
  }))
}

function failureAction(t: (key: string, options?: Record<string, unknown>) => unknown, code: string) {
  return String(t(`pages.inventoryZohoPullRunDetail.failureCodes.${code}.action`, {
    defaultValue: String(t('pages.inventoryZohoPullRunDetail.failureCodes.UNKNOWN.action')),
  }))
}

function MissingValue({ value, fallback }: { value?: string | null; fallback: string }) {
  return <span style={{ fontFamily: value ? 'var(--font-mono)' : 'inherit', color: value ? 'var(--fg)' : 'var(--fg-subtle)', ...TEXT_WRAP_STYLE }}>{value || fallback}</span>
}

function IssueMobileField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
      <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ color: 'var(--fg)', fontSize: 13, ...TEXT_WRAP_STYLE }}>{children}</div>
    </div>
  )
}

export function InventoryZohoPullRunDetailPage({ accessToken, inventoryConnectionId, runId, onBack }: InventoryZohoPullRunDetailPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [pageIndex, setPageIndex] = useState(0)
  const [selectedCode, setSelectedCode] = useState<string | null>(null)
  const [queryInput, setQueryInput] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState('')
  const requestKey = inventoryConnectionId == null || runId == null
    ? null
    : `${accessToken}:${inventoryConnectionId}:${runId}:${pageIndex}:${selectedCode ?? ''}:${submittedQuery}`
  const [requestState, setRequestState] = useState<{
    key: string | null
    pageData: InventoryProductPullRunIssuesPage | null
    error: unknown
  }>({ key: null, pageData: null, error: null })

  useEffect(() => {
    if (requestKey == null || inventoryConnectionId == null || runId == null) return
    let cancelled = false
    getInventoryProductPullRunIssues(accessToken, inventoryConnectionId, runId, {
      page: pageIndex,
      size: PAGE_SIZE,
      code: selectedCode,
      query: submittedQuery,
    })
      .then(next => {
        if (!cancelled) setRequestState({ key: requestKey, pageData: next, error: null })
      })
      .catch(caught => {
        if (!cancelled) setRequestState({ key: requestKey, pageData: null, error: caught })
      })
    return () => { cancelled = true }
  }, [accessToken, inventoryConnectionId, requestKey, runId, pageIndex, selectedCode, submittedQuery])

  function searchIssues(event: FormEvent) {
    event.preventDefault()
    setPageIndex(0)
    setSubmittedQuery(queryInput.trim())
  }

  function chooseCode(code: string | null) {
    setSelectedCode(code)
    setPageIndex(0)
  }

  const pageData = requestState.key === requestKey ? requestState.pageData : null
  const error = requestState.key === requestKey ? requestState.error : null
  const loading = requestKey != null && requestState.key !== requestKey
  const run: InventoryProductPullRun | null = pageData?.run ?? null
  const groups: InventoryProductPullRunIssueGroup[] = pageData?.groups ?? []
  const items: InventoryProductPullRunIssue[] = pageData?.items ?? []
  const total = pageData?.totalElements ?? 0
  const totalPages = pageData?.totalPages ?? 0
  const pageStart = total === 0 ? 0 : pageIndex * PAGE_SIZE + 1
  const pageEnd = Math.min(total, pageIndex * PAGE_SIZE + items.length)
  const noDetailsForHistoricalRun = Boolean(run && groups.length === 0 && items.length === 0 && (run.issuesCount > 0 || run.productsSkipped > 0 || run.stockRowsSkipped > 0))
  const failedWithoutItemRows = Boolean(run && run.status === 'FAILED' && groups.length === 0 && items.length === 0 && !noDetailsForHistoricalRun)
  const emptyCleanRun = Boolean(run && groups.length === 0 && items.length === 0 && !noDetailsForHistoricalRun && !failedWithoutItemRows)

  if (inventoryConnectionId == null || runId == null) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack}>{t('pages.inventoryZohoPullRunDetail.back')}</Button>
        <Card padding={24} style={{ borderStyle: 'dashed', background: 'transparent' }}>
          <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{t('pages.inventoryZohoPullRunDetail.missing.title')}</div>
          <div style={{ marginTop: 6, color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.inventoryZohoPullRunDetail.missing.body')}</div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack}>{t('pages.inventoryZohoPullRunDetail.back')}</Button>
        {run && (
          <Pill tone={runTone(run.status)} dot>{t(`providerSyncProgress.status.${run.status}`)}</Pill>
        )}
      </div>

      <Card padding={22}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 14, alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: 'var(--brand-900)' }}>{t('pages.inventoryZohoPullRunDetail.title')}</h2>
              {run && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-subtle)' }}>{t('pages.inventoryZohoPullRunDetail.runId', { id: run.id })}</span>}
            </div>
            <div style={{ marginTop: 6, color: 'var(--fg-muted)', fontSize: 13.5, lineHeight: 1.5 }}>
              {t('pages.inventoryZohoPullRunDetail.subtitle')}
            </div>
          </div>
        </div>
      </Card>

      {error != null && (
        <Card padding={14} style={{ borderColor: 'var(--danger-100)', background: 'var(--danger-100)' }}>
          <div style={{ color: 'var(--danger)', fontWeight: 800, fontSize: 13 }}>
            {String(t(errorKey(error), { defaultValue: String(t('pages.inventoryZohoPullRunDetail.errors.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      {loading && !pageData ? (
        <Card padding={24} style={{ borderStyle: 'dashed', background: 'transparent' }}>
          <div style={{ color: 'var(--fg-muted)', fontSize: 13, fontWeight: 800 }}>{t('pages.inventoryZohoPullRunDetail.loading')}</div>
        </Card>
      ) : run ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <Stat label={t('pages.inventoryZohoPullRunDetail.stats.fetched')} value={String(run.fetched)} body={t('pages.inventoryZohoPullRunDetail.stats.pages', { count: run.pageCount })} />
            <Stat label={t('pages.inventoryZohoPullRunDetail.stats.products')} value={String(run.productsCreated + run.productsUpdated)} body={t('pages.inventoryZohoPullRunDetail.stats.productsBody', { created: run.productsCreated, updated: run.productsUpdated })} />
            <Stat label={t('pages.inventoryZohoPullRunDetail.stats.skipped')} value={String(run.productsSkipped + run.stockRowsSkipped)} body={t('pages.inventoryZohoPullRunDetail.stats.skippedBody', { products: run.productsSkipped, stock: run.stockRowsSkipped })} />
            <Stat label={t('pages.inventoryZohoPullRunDetail.stats.issues')} value={String(run.issuesCount)} body={t('pages.inventoryZohoPullRunDetail.stats.updated', { date: formatDateTime(run.finishedAt ?? run.updatedAt, i18n.language, String(t('pages.inventoryZohoPullRunDetail.notAvailable'))) })} />
          </div>

          {run.status === 'FAILED' && (
            <Card padding={18} style={{ borderColor: 'var(--danger-100)', background: 'var(--danger-100)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 10, alignItems: 'start' }}>
                <Icon size={18} color="var(--danger)">{I.alert}</Icon>
                <div>
                  <div style={{ fontWeight: 900, color: 'var(--danger)', fontSize: 14 }}>{failureTitle(t, run.errorCode ?? 'UNKNOWN')}</div>
                  <div style={{ marginTop: 4, color: 'var(--fg)', fontSize: 13, lineHeight: 1.55 }}>{failureAction(t, run.errorCode ?? 'UNKNOWN')}</div>
                  <div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', color: 'var(--danger)', fontSize: 12 }}>{run.errorCode ?? t('pages.inventoryZohoPullRunDetail.failureCodes.UNKNOWN.code')}</div>
                </div>
              </div>
            </Card>
          )}

          <Card padding={0}>
            <div style={{ padding: 18, borderBottom: '1px solid var(--border-subtle)', display: 'grid', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 900, color: 'var(--fg)', fontSize: 15 }}>{t('pages.inventoryZohoPullRunDetail.groups.title')}</div>
                <div style={{ marginTop: 3, color: 'var(--fg-muted)', fontSize: 12.5 }}>{t('pages.inventoryZohoPullRunDetail.groups.subtitle')}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button kind={selectedCode == null ? 'primary' : 'secondary'} size="xs" onClick={() => chooseCode(null)}>{t('pages.inventoryZohoPullRunDetail.groups.allReasons')}</Button>
                {groups.map(group => (
                  <Button key={group.code} kind={selectedCode === group.code ? 'primary' : 'secondary'} size="xs" onClick={() => chooseCode(group.code)}>
                    {t('pages.inventoryZohoPullRunDetail.groups.reasonFilter', { reason: issueTitle(t, group.code), count: group.count })}
                  </Button>
                ))}
              </div>
            </div>

            {groups.length === 0 ? (
              <div style={{ padding: 20, color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.inventoryZohoPullRunDetail.groups.empty')}</div>
            ) : (
              <div style={{ display: 'grid', gap: 0 }}>
                {groups.map(group => (
                  <div key={group.code} style={{ padding: '14px 18px', borderTop: '1px solid var(--border-subtle)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 12, alignItems: 'center' }}>
                    <div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 900, color: 'var(--brand-900)', fontSize: 14 }}>{issueTitle(t, group.code)}</div>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-subtle)' }}>{group.code}</span>
                      </div>
                      <div style={{ marginTop: 4, color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.5 }}>{issueAction(t, group.code)}</div>
                      <div style={{ marginTop: 6, color: 'var(--fg-subtle)', fontSize: 12 }}>
                        {t('pages.inventoryZohoPullRunDetail.groups.sample', {
                          sku: group.sampleSku || t('pages.inventoryZohoPullRunDetail.notAvailable'),
                          external: group.sampleExternalProductId || t('pages.inventoryZohoPullRunDetail.notAvailable'),
                        })}
                      </div>
                    </div>
                    <Pill tone="warning">{t('pages.inventoryZohoPullRunDetail.groups.count', { count: group.count })}</Pill>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card padding={0}>
            <div style={{ padding: 18, borderBottom: '1px solid var(--border-subtle)', display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 900, color: 'var(--fg)', fontSize: 15 }}>{t('pages.inventoryZohoPullRunDetail.items.title')}</div>
                  <div style={{ marginTop: 3, color: 'var(--fg-muted)', fontSize: 12.5 }}>{t('pages.inventoryZohoPullRunDetail.items.subtitle')}</div>
                </div>
                <div style={{ color: 'var(--fg-muted)', fontSize: 12.5 }}>
                  {t('pages.inventoryZohoPullRunDetail.pagination.range', { start: pageStart, end: pageEnd, total })}
                </div>
              </div>
              <form onSubmit={searchIssues} style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 8 }}>
                <input
                  value={queryInput}
                  onChange={event => setQueryInput(event.target.value)}
                  placeholder={t('pages.inventoryZohoPullRunDetail.items.searchPlaceholder')}
                  style={{ border: '1px solid var(--border-default)', borderRadius: 12, padding: '9px 12px', font: 'inherit', color: 'var(--fg)', background: 'white', minWidth: 0 }}
                />
                <Button kind="secondary" size="sm" icon={I.search} type="submit" style={mobileButtonStyle(isMobile)}>{t('pages.inventoryZohoPullRunDetail.items.search')}</Button>
              </form>
            </div>

            {loading ? (
              <div style={{ padding: 26, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 800 }}>{t('pages.inventoryZohoPullRunDetail.loading')}</div>
            ) : noDetailsForHistoricalRun ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.55 }}>
                <div style={{ fontWeight: 900, color: 'var(--fg)', marginBottom: 4 }}>{t('pages.inventoryZohoPullRunDetail.empty.historicalTitle')}</div>
                <div>{t('pages.inventoryZohoPullRunDetail.empty.historicalBody')}</div>
              </div>
            ) : failedWithoutItemRows ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.55 }}>
                <div style={{ fontWeight: 900, color: 'var(--fg)', marginBottom: 4 }}>{t('pages.inventoryZohoPullRunDetail.empty.failedNoItemsTitle')}</div>
                <div>{t('pages.inventoryZohoPullRunDetail.empty.failedNoItemsBody')}</div>
              </div>
            ) : emptyCleanRun ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.55 }}>
                <div style={{ fontWeight: 900, color: 'var(--fg)', marginBottom: 4 }}>{t('pages.inventoryZohoPullRunDetail.empty.cleanTitle')}</div>
                <div>{t('pages.inventoryZohoPullRunDetail.empty.cleanBody')}</div>
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.55 }}>
                <div style={{ fontWeight: 900, color: 'var(--fg)', marginBottom: 4 }}>{t('pages.inventoryZohoPullRunDetail.empty.filteredTitle')}</div>
                <div>{t('pages.inventoryZohoPullRunDetail.empty.filteredBody')}</div>
              </div>
            ) : (
              isMobile ? (
                <div style={{ display: 'grid' }}>
                  {items.map(item => (
                    <div key={item.id} style={{ display: 'grid', gap: 12, padding: 16, borderTop: '1px solid var(--border-subtle)' }}>
                      <div style={TEXT_WRAP_STYLE}>
                        <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{issueTitle(t, item.code)}</div>
                        <div style={{ marginTop: 2, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-subtle)' }}>{item.code}</div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: MOBILE_SINGLE_COLUMN, gap: 10 }}>
                        <IssueMobileField label={t('pages.inventoryZohoPullRunDetail.table.sku')}>
                          <MissingValue value={item.sku} fallback={t('pages.inventoryZohoPullRunDetail.notAvailable')} />
                        </IssueMobileField>
                        <IssueMobileField label={t('pages.inventoryZohoPullRunDetail.table.externalProductId')}>
                          <MissingValue value={item.externalProductId} fallback={t('pages.inventoryZohoPullRunDetail.notAvailable')} />
                        </IssueMobileField>
                        <IssueMobileField label={t('pages.inventoryZohoPullRunDetail.table.page')}>
                          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-muted)' }}>{item.pageNumber}</span>
                        </IssueMobileField>
                        <IssueMobileField label={t('pages.inventoryZohoPullRunDetail.table.row')}>
                          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-muted)' }}>{item.sequenceNumber}</span>
                        </IssueMobileField>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
                    <thead>
                      <tr style={{ background: 'var(--ink-50)' }}>
                        {[
                          t('pages.inventoryZohoPullRunDetail.table.reason'),
                          t('pages.inventoryZohoPullRunDetail.table.sku'),
                          t('pages.inventoryZohoPullRunDetail.table.externalProductId'),
                          t('pages.inventoryZohoPullRunDetail.table.page'),
                          t('pages.inventoryZohoPullRunDetail.table.row'),
                        ].map(header => (
                          <th key={header} style={{ textAlign: 'start', padding: '11px 16px', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                          <td style={{ padding: '13px 16px' }}>
                            <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{issueTitle(t, item.code)}</div>
                            <div style={{ marginTop: 2, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-subtle)' }}>{item.code}</div>
                          </td>
                          <td style={{ padding: '13px 16px' }}><MissingValue value={item.sku} fallback={t('pages.inventoryZohoPullRunDetail.notAvailable')} /></td>
                          <td style={{ padding: '13px 16px' }}><MissingValue value={item.externalProductId} fallback={t('pages.inventoryZohoPullRunDetail.notAvailable')} /></td>
                          <td style={{ padding: '13px 16px', fontFamily: 'var(--font-mono)', color: 'var(--fg-muted)' }}>{item.pageNumber}</td>
                          <td style={{ padding: '13px 16px', fontFamily: 'var(--font-mono)', color: 'var(--fg-muted)' }}>{item.sequenceNumber}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            <div style={{ padding: 14, borderTop: '1px solid var(--border-subtle)', display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', gap: 10, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
              <div style={{ color: 'var(--fg-muted)', fontSize: 12.5 }}>
                {t('pages.inventoryZohoPullRunDetail.pagination.page', { page: totalPages === 0 ? 0 : pageIndex + 1, totalPages })}
              </div>
              <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8 }}>
                <Button kind="secondary" size="sm" disabled={pageIndex <= 0 || loading} onClick={() => setPageIndex(current => Math.max(0, current - 1))} style={mobileButtonStyle(isMobile)}>{t('pages.inventoryZohoPullRunDetail.pagination.previous')}</Button>
                <Button kind="secondary" size="sm" disabled={pageIndex + 1 >= totalPages || loading} onClick={() => setPageIndex(current => current + 1)} style={mobileButtonStyle(isMobile)}>{t('pages.inventoryZohoPullRunDetail.pagination.next')}</Button>
              </div>
            </div>
          </Card>
        </>
      ) : null}
    </div>
  )
}
